/**
 * Client-side forecast for an arbitrary lat/lon — used by the long-press
 * "drop a pin" interaction on the spots map. Hits `/api/point`, which
 * runs the same merged SMHI + Open-Meteo pipeline that `/api/forecast`
 * uses for curated spots. See
 * docs/decisions.md → "One wind data pipeline for every location" for
 * why we never fetch SMHI / Open-Meteo directly from the browser.
 *
 * The endpoint returns raw daytime summaries and band data (no
 * scoring — an arbitrary point has no curated ideal/secondary
 * directions to compare against). This module shapes that response
 * into an EnrichedSpot so MapSpotCard can render it without a
 * parallel code path.
 */

import { useQuery } from "@tanstack/react-query";
import type { Band, CompassDir, Coords, EnrichedSpot, ScoredDay, ScoredHour } from "./types";
import { COMPASS_DIRS } from "./types";

/**
 * Error thrown when `/api/point` returns a non-2xx status. Carries the
 * HTTP status so callers can distinguish an unservable coordinate
 * (400 — inland / outside coverage, retrying won't help) from a
 * transient upstream/server failure (5xx — worth a retry).
 */
export class PointForecastError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`Point forecast error: ${status}`);
    this.name = "PointForecastError";
    this.status = status;
  }
}

interface PointHour {
  speedMs: number;
  dirDeg: number;
  gustMs: number;
  tempC: number | null;
  time: string;
  isNow: boolean;
}

interface PointSummary {
  speedMinMs: number;
  speedMaxMs: number;
  speedMeanMs: number;
  gustMaxMs: number;
  dirDeg: number;
  dirSet: string[];
}

interface PointBand {
  meanSpeed: number;
  dirDeg: number;
}

interface PointDay {
  date: string;
  dayOffset: number;
  summary: PointSummary | null;
  bandData: Array<PointBand | null>;
  rawHours: Array<{
    time: string;
    speedMs: number;
    dirDeg: number;
    gustMs: number;
    isNow: boolean;
  }>;
}

interface PointResponse {
  fetchedAt: string;
  coords: Coords;
  wind: PointHour;
  windHours: PointHour[];
  days: PointDay[];
}

function toScoredDay(day: PointDay): ScoredDay {
  const hours: ScoredHour[] = day.rawHours.map((h) => ({
    time: h.time,
    speedMs: h.speedMs,
    dirDeg: h.dirDeg,
    gustMs: h.gustMs,
    // No tempC at the per-day hour level — the wind hour stream above
    // carries temperature; the per-day rawHours don't.
    tempC: null,
    isNow: h.isNow,
    score: 0,
    level: "unlikely",
  }));

  if (!day.summary) {
    return {
      date: day.date,
      dayOffset: day.dayOffset,
      level: "skip",
      score: 0,
      bands: [],
      hours: [],
    };
  }

  const summary = day.summary;
  const bands: Band[] = day.bandData.map((b) => {
    if (b) return { score: 0, level: "unlikely", meanSpeedMs: b.meanSpeed };
    // Empty band — paint with the day's mean so a partial day still
    // renders four blobs (matches the curated-spot behaviour).
    return { score: 0, level: "unlikely", meanSpeedMs: summary.speedMeanMs };
  });

  return {
    date: day.date,
    dayOffset: day.dayOffset,
    speedMinMs: summary.speedMinMs,
    speedMaxMs: summary.speedMaxMs,
    gustMaxMs: summary.gustMaxMs,
    dirDeg: summary.dirDeg,
    dirSet: summary.dirSet.filter((d): d is CompassDir =>
      (COMPASS_DIRS as readonly string[]).includes(d),
    ),
    score: 0,
    matchType: "bad",
    level: "unlikely",
    bands,
    hours,
  };
}

async function fetchCustomLocation(coords: Coords): Promise<EnrichedSpot> {
  const params = new URLSearchParams({
    lat: String(coords.lat),
    lon: String(coords.lon),
  });
  const res = await fetch(`/api/point?${params}`);
  if (!res.ok) throw new PointForecastError(res.status);
  const json = (await res.json()) as PointResponse;

  const days = json.days.map(toScoredDay);

  return {
    id: customLocationId(coords),
    name: formatCoords(coords),
    region: "stockholm", // unused — MapSpotCard reads area/name only
    area: "Custom location",
    coords,
    ideal: { dirs: [], speed_min: 0, speed_max: 0 },
    secondary: { dirs: [], speed_min: 0, speed_max: 0 },
    priority: 0,
    score: 0,
    matchType: "bad",
    wind: json.wind,
    windHours: json.windHours,
    days,
  };
}

export function customLocationId(coords: Coords): string {
  return `__custom_${coords.lat.toFixed(4)}_${coords.lon.toFixed(4)}`;
}

export function formatCoords(coords: Coords): string {
  const lat = coords.lat.toFixed(2);
  const lon = coords.lon.toFixed(2);
  return `${lat}, ${lon}`;
}

export function useCustomLocationForecast(coords: Coords | null) {
  return useQuery({
    queryKey: ["customLocation", coords?.lat, coords?.lon],
    queryFn: () => fetchCustomLocation(coords!),
    enabled: coords !== null,
    staleTime: 10 * 60 * 1000,
    // A 400 means the coordinate itself is unservable (inland / outside
    // coverage) — retrying won't change the answer. Only retry transient
    // failures, and only once.
    retry: (failureCount, error) => {
      if (error instanceof PointForecastError && error.status === 400) {
        return false;
      }
      return failureCount < 1;
    },
  });
}
